import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  Toolbar,
  AppBar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import api from "../services/api";

/**
 * ShortlistThanksPage is a React component that renders a page to thank
 * the candidate for being shortlisted. It provides options to schedule an
 * interview or start applying for the job.
 *
 * @return {JSX.Element} The rendered ShortlistThanksPage component.
 */
export default function ShortlistThanksPage() {
  const { token } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [resumeId, setResumeId] = useState(null);
  const [jdId, setJdId] = useState(null);
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    title: "",
    message: "",
    type: "info"
  });

  const [availability, setAvailability] = useState({
    date: "",
    time: "",
  });
  const navigate = useNavigate();

  const [jd, setJd] = useState(null);

  /**
   * Resolves the token and sets the resume and job details.
   *
   * @return {Promise<void>} A Promise that resolves when the job details are fetched.
   */
  useEffect(() => {
    if (!token) return;

    const resolveToken = async () => {
      try {
        const res = await api.get(`/resolve-token/${token}/`);
        setResumeId(res.data.resume_id);
        setJdId(res.data.jd_id);
      } catch {
        navigate("Invalid Job Link");
      }
    };

    resolveToken();
  }, [token]);

  /**
   * Fetches the job details based on the resume and job IDs.
   *
   * @return {Promise<void>} A Promise that resolves when the job details are fetched.
   */
  useEffect(() => {
    if (!resumeId || !jdId) return;

    const value = `${resumeId}_${jdId}`;

    api.get(`/shortlist-details/${value}/`)
      .then(res => {
        setJd(res.data);
        console.log("setJd", res.data)
      })
      .catch(err => {
        console.error(err);
      });
  }, [resumeId, jdId]);

  /**
   * Handles the start now click event by opening the interview welcome page.
   *
   * @return {void}
   */
  const handleStartNow = () => {
    if (!resumeId || !jdId) return;

    window.open(`${api.interview_link}/interview_welcome?token=${token}`);
  };

  /**
   * Handles the schedule click event by opening the dialog.
   *
   * @return {void}
   */
  const handleScheduleClick = () => {
    setOpenDialog(true);
  };

  /**
   * Closes the dialog.
   *
   * @return {void}
   */
  const handleClose = () => {
    setOpenDialog(false);
  };

  /**
   * Submits the candidate's availability to the server.
   *
   * @return {Promise<void>} A Promise that resolves when the availability is submitted.
   * @throws {Error} If the availability submission fails.
   */
  const handleSubmitAvailability = () => {
    api.post("/candidate-availability/", {
      token,
      date: availability.date,
      time: availability.time,
    })
      .then(() => {
        setStatusDialog({
          open: true,
          title: "Success",
          message: "Your availability has been submitted successfully!",
          type: "success"
        });
        setOpenDialog(false);
      })
      .catch(err => {
        console.error(err);
        setStatusDialog({
          open: true,
          title: "Error",
          message: "Something went wrong. Please try again later.",
          type: "error"
        });
      });
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.9)",
          borderBottom: "1px solid #eee",
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              background: "linear-gradient(90deg,#6366f1,#ec4899)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            ITConnectUS
          </Typography>
        </Toolbar>
      </AppBar>
      <Box display="flex" justifyContent="center" mt={8}>
        <Card sx={{ p: 4, maxWidth: 900, width: "100%" }}>
          {jd && !jd.already_submitted && (
            <Typography variant="h5" gutterBottom>
              Thank you for the Additonal Info!
            </Typography>
          )}
          {jd && jd.already_submitted && (
            <Typography variant="h5" gutterBottom>
              Thank you for your interest in this opportunity!
            </Typography>
          )}

          {jd && (
            <>
              <Typography variant="h6" mt={2}>
                {jd.job_title}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Location: {jd.job_location}
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={3}>
                Experience: {jd.years_of_experience}
              </Typography>
            </>
          )}


          {jd?.required_l0 === true && (<>
            <Typography variant="body1" mb={3}>
              As part of the selection process, you are required to complete an
              AI pre-screening video interview.
            </Typography>

            <Typography variant="body1" mb={3}>
              The pre-screening interview contains up to 10 standard questions relevant to your experience or resume.
              <br />
              It will take 10–30 minutes.
            </Typography>

            <Typography variant="body1" mb={3}>
              Please select one of the following options:
            </Typography>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                sx={{ textTransform: "none" }}
                onClick={handleStartNow}
              >
                Start Interview Now
              </Button>

              <Button
                variant="contained"
                sx={{ textTransform: "none" }}
                onClick={handleScheduleClick}
              >
                Schedule Interview
              </Button>
            </Box>
          </>)}

        </Card>
        <Dialog open={openDialog} onClose={handleClose} fullWidth>
          <DialogTitle>Schedule Your Interview</DialogTitle>

          <DialogContent>
            <TextField
              label="Preferred Date *"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={availability.date}
              onChange={(e) =>
                setAvailability({ ...availability, date: e.target.value })
              }
            />

            <TextField
              label="Preferred Time *"
              type="time"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={availability.time}
              onChange={(e) =>
                setAvailability({ ...availability, time: e.target.value })
              }
            />
          </DialogContent>

          <DialogActions>
            <Button variant="contained" sx={{ textTransform: "none" }} color="primary" onClick={handleClose}>Cancel</Button>
            <Button variant="contained" sx={{ textTransform: "none" }} color="primary" onClick={handleSubmitAvailability}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={statusDialog.open}
          onClose={() => setStatusDialog({ ...statusDialog, open: false })}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              color:
                statusDialog.type === "error"
                  ? "#ff4d4d"
                  : statusDialog.type === "success"
                    ? "#16a34a"
                    : "inherit"
            }}
          >
            {statusDialog.title}
          </DialogTitle>

          <DialogContent>
            <Typography fontSize="14px">
              {statusDialog.message}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                setStatusDialog({ ...statusDialog, open: false });
                if (statusDialog.type === "success") {
                  setOpenDialog(false);
                }
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}